import React, { useContext, useEffect, useState, useRef } from "react";
import AuthContext from "contexts/authContext";
import { getAPI, postAPI } from "apis/backend/apiNode";

import { searchClientPartner } from "odooApi/clientPartner";
import searchPlanning, { getSinglePlanning } from "odooApi/planningSlot";
import { searchMailMessage } from "odooApi/mailMessage";
import showNotification, { removeNotification } from "components/extras/ringingNotif";
import Icon from "components/icon/Icon";
import OffCanvas, { OffCanvasBody, OffCanvasHeader } from "components/bootstrap/OffCanvas";
import {
  formatClientData,
  validateClientForm,
  clientCreationFields, // ⬅️ ensure this export exists in fieldsConfig
} from "components/common/fieldsConfig";
import CreationModal from "components/common/CreationModal";
import { Badge, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import moment from "moment";
import { usePlanningData } from "pages/planning/hooks/usePlanningData";
import InterventionDetailsModal from "pages/planning/components/InterventionDetailsModal";

export default function Events() {
  const { userData, socket } = useContext(AuthContext);

  // ============================================================
  // State variables
  // ============================================================
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Creation modal inside offcanvas (for nouveau client)
  const [showCreationModal, setShowCreationModal] = useState(false);

  // Intervention detail modal (for DB interventions)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Odoo intervention detail modal (messages)
  const [odooModalOpen, setOdooModalOpen] = useState(false);
  const [selectedOdooInt, setSelectedOdooInt] = useState(null);
  const [mailMessages, setMailMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Cache & active calls
  const clientCacheRef = useRef({});
  const activeCallsRef = useRef({});
  const {
    cars,
    allCars,
    clients,
    techniciens,
    loading,
    filters,
    interventions,
    updateFilters,
    addIntervention,
    updateIntervention,
    deleteIntervention,
    transferInterventions,
    getInterventionsByCarAndDate,
    getChronologieByIntervention,
    getRapportByIntervention,
    getStatusColor,
    getStatusTextColor,
    addChronologieEntry,
    CHRONOLOGIE_TYPES,
    INTERVENTION_TYPES,
    INTERVENTION_MOTIFS,
    reloadData,
    getInterventionById,
    refreshIntervention,
    updateRapport,
    deleteRapport,
    createRapport,
    addPaiement,
    getPaiementsByRapport,
    updatePaiement,
    deletePaiement,
    loadRapportByIntervention,
    loadCars,
    getGroupedTechniciens,
  } = usePlanningData();

  // ============================================================
  // Helper functions
  // ============================================================
  const formatPhoneNumber = (num) => {
    if (!num) return "";
    return num.replace(/^00/, "+");
  };

  const handleCloseSearchModal = () => {
    setSearchModalOpen(false);
    setSearchResults(null);
    setSearchQuery("");
    setShowCreationModal(false); // close creation modal when offcanvas closes
  };

  // ============================================================
  // Socket events (ringing, answered, hangup)
  // ============================================================
  useEffect(() => {
    if (!socket) return;

    const currentRingoverId = String(userData?.ringover_id || "");

    const preloadClientData = async (fromNumber) => {
      // ... (same as your preloadClientData but returns full client object)
      try {
        const res = await getAPI("store2000_client", `/phone/${fromNumber}`);
        if (res?.client) {
          const dbInterventions = await loadClientInterventions(res.client);
          const odooData = await getOdooInterventions(fromNumber);
          const formattedOdoo = formatOdooInterventions(odooData?.interventions || []);
          const merged = [...dbInterventions, ...formattedOdoo].sort((a, b) => new Date(b.date_debut) - new Date(a.date_debut));
          const full = { ...res.client, telephone: fromNumber, interventions: merged, odoo: odooData?.customer };
          clientCacheRef.current[fromNumber] = full;
          return full;
        }
        const odooData = await getOdooInterventions(fromNumber);
        if (odooData?.customer) {
          const fake = { nom: odooData.customer.name, telephone: fromNumber, origin: "odoo", interventions: formatOdooInterventions(odooData.interventions), odoo: odooData.customer };
          clientCacheRef.current[fromNumber] = fake;
          return fake;
        }
        const newClient = { telephone: fromNumber, origin: "new", interventions: [] };
        clientCacheRef.current[fromNumber] = newClient;
        return newClient;
      } catch {
        const fallback = { telephone: fromNumber, origin: "new", interventions: [] };
        clientCacheRef.current[fromNumber] = fallback;
        return fallback;
      }
    };

    const openOffcanvasWithClient = async (fromNumber) => {

      try {
        let clientData = clientCacheRef.current[fromNumber];
        if (!clientData) clientData = await preloadClientData(fromNumber);
        if (clientData.origin === "new") {
          // Show creation modal inside offcanvas
          return;
        } else {
          setSearchLoading(true);
          setSearchResults(clientData);
          setSearchModalOpen(true);
          setSearchQuery(fromNumber);
          setShowCreationModal(false);
          
        }
       
      } catch (error) {
        console.error("Failed to load client:", error);
        setSearchResults({ telephone: fromNumber, origin: "new", interventions: [] });
        setShowCreationModal(false);
      } finally {
        setSearchLoading(false);
      }
    };

    const handleRinging = async (data) => {
      if (data?.user_id && String(data.user_id) !== currentRingoverId) return;
      const callId = data?.ringover_call_id || data?.call_id;
      const fromNumber = data?.from_number;
      if (!callId || !fromNumber) return;
      if (activeCallsRef.current[callId]?.ringingShown) return;

      activeCallsRef.current[callId] = { callId, fromNumber, ringingShown: true };
      try {
        const clientInfo = await preloadClientData(fromNumber);
        showNotification(
          callId,
          <span><Icon icon='call' /> Appel entrant</span>,
          `${!!clientInfo.nom ? clientInfo.nom : fromNumber} (${clientInfo.origin === "new" ? "Nouveau Client" : "Ancien Client"})`,
          "danger",
          3000
        );
      } catch {
        showNotification(callId, <span><Icon icon='call' /> Appel entrant</span>, fromNumber, "danger", 3000);
      }
    };

    const handleAnswered = async (data) => {
      if (data?.direction === "in" && String(data?.user_id) === currentRingoverId) {
        const callId = data?.ringover_call_id || data?.call_id;
        const fromNumber = data?.from_number;
        if (!callId || !fromNumber) return;
        removeNotification(callId);
        activeCallsRef.current[callId] = { ...activeCallsRef.current[callId], answeredShown: true };
        await openOffcanvasWithClient(fromNumber);
      }
    };

    const handleHangup = (data) => {
      if (data?.user_id && String(data.user_id) !== currentRingoverId) return;
      const callId = data?.ringover_call_id || data?.call_id;
      if (callId) {
        removeNotification(callId);
        delete activeCallsRef.current[callId];
      }
      setSearchModalOpen(false);
      setShowCreationModal(false);
    };

    socket.on("ringover:ringing", handleRinging);
    socket.on("ringover:answered", handleAnswered);
    socket.on("ringover:hangup", handleHangup);

    return () => {
      socket.off("ringover:ringing", handleRinging);
      socket.off("ringover:answered", handleAnswered);
      socket.off("ringover:hangup", handleHangup);
    };
  }, [socket, userData?.ringover_id]);

  // ============================================================
  // Data fetching helpers (unchanged but moved)
  // ============================================================
  const loadClientInterventions = async (clientData) => {
    if (!clientData?._id) return [];
    const res = await getAPI("planning", `/intervention/client/${clientData._id}`);
    return res?.interventions || res?.data?.interventions || [];
  };

  const getOdooInterventions = async (fromNumber) => {
    if (!fromNumber) return { customer: null, interventions: [] };
    const digits = fromNumber.replace(/\D/g, "");
    let searchQueryOdoo = fromNumber;
    if (digits.length >= 9) searchQueryOdoo = digits.slice(-9);
    const filter = ["|", ["mobile", "ilike", searchQueryOdoo], ["phone", "ilike", searchQueryOdoo]];
    const customers = await new Promise((resolve) => {
      searchClientPartner("014c8771-bfcd-4300-811c-366bd4861b86", filter, ({ elements }) => resolve(elements || []), () => resolve([]));
    });
    const customer = customers?.[0] || null;
    if (!customer?.id) return { customer: null, interventions: [] };
    const planning = await new Promise((resolve) => {
      searchPlanning("014c8771-bfcd-4300-811c-366bd4861b86", [["partner_id.id", "=", customer.id]], ({ elements }) => resolve(elements || []), () => resolve([]));
    });
    return { customer, interventions: planning };
  };

  const formatOdooInterventions = (odooInterventions = []) => {
    return odooInterventions.map((item) => ({
      _id: `odoo-${item.id}`,
      reference: item.name_seq || item.name || "Sans référence",
      type: item.type_int || "",
      motif: item.motif || "",
      techniciens: item.employees_ids || [],
      date_debut: item.start_datetime || null,
      date_fin: item.end_datetime || null,
      statut: item.state || "",
      createdBy: item.login_user || "",
      origin: "odoo",
      notes: [{ note: item.Remarque || "" }],
    }));
  };

  // ============================================================
  // Intervention detail handlers
  // ============================================================
  const handleViewIntervention = async (intervention) => {
    if (intervention?.origin === "odoo") {
      setSelectedOdooInt(intervention);
      setOdooModalOpen(true);
      setLoadingMessages(true);
      const filter = ['&', ['res_id', '=', intervention?._id], ['record_name', '=', intervention?.reference]];
      searchMailMessage("7aa64b90-50b7-4204-b98e-7b08bef88a4b", filter, ({ elements }) => {
        setMailMessages(elements || []);
        setLoadingMessages(false);
      }, () => setLoadingMessages(false));
      return;
    }
    // close offcanvas if needed
    // setSearchModalOpen(false);
    setDetailsLoading(true);
    try {
      const result = await getAPI('planning', `/intervention/${intervention._id}`);
      if (result?.success && result.data) {
        setSelectedIntervention(result.data);
        setDetailsModalOpen(true);
      } else {
        showNotification('Erreur', 'Impossible de charger les détails', 'warning');
      }
    } catch (error) {
      console.error('Error loading intervention details:', error);
      showNotification('Erreur', 'Erreur lors du chargement', 'danger');
    } finally {
      setDetailsLoading(false);
    }
  };

  // ============================================================
  // Client creation (nouveau client)
  // ============================================================
  const handleCreateClient = async (clientFormData) => {
    try {
      const errors = validateClientForm(clientFormData);
      if (Object.keys(errors).length > 0) throw new Error('Validation échouée');
      const clientData = formatClientData(clientFormData);
      const response = await postAPI('store2000_client', clientData, '');
      if (response.success) {
        showNotification('Succès', 'Client créé avec succès', 'success');
        // Refresh offcanvas with newly created client
        const newClient = response.client || response.data;
        setSearchResults((prev) => ({ ...newClient, telephone: prev?.telephone, interventions: prev?.interventions || [] }));
        setShowCreationModal(false);
        return newClient;
      } else {
        throw new Error(response.error || 'Erreur création');
      }
    } catch (error) {
      console.error(error);
      showNotification('Erreur', error.message || 'Création échouée', 'danger');
      throw error;
    }
  };

  // ============================================================
  // Rendering helpers (inside component to access state)
  // ============================================================
  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'planifie':
      case 'draft':
        return <span className={`p-1 rounded-2 bg-l10-info text-info`}><Icon icon="Calendar3" className="me-1" /> Planifié</span>;
      case 'en_cours':
      case 'sale':
        return <span className={`p-1 rounded-2 bg-l10-warning text-warning`}><Icon icon="Clock" className="me-1" /> En cours</span>;
      case 'termine':
      case 'done':
        return <span className={`p-1 rounded-2 bg-l10-success text-success`}><Icon icon="CheckCircle" className="me-1" /> Terminé</span>;
      case 'annule':
      case 'cancel':
        return <span className={`p-1 rounded-2 bg-l10-danger text-danger`}><Icon icon="XCircle" className="me-1" /> Annulé</span>;
      default:
        return <span className={`p-1 rounded-2 bg-l10-secondary text-secondary`}>{statut}</span>;
    }
  };
  const getTypeBadge = (type) => {
    const icons = {
      devis: 'FileEarmarkText',
      intervention: 'Tools',
      pose: 'Hammer'
    };
    const labels = {
      devis: 'Devis',
      intervention: 'Intervention',
      pose: 'Pose'
    };

    return (
      <span className={`p-1 rounded-2 bg-l10-info text-info p-1`}>
        {/* <Icon icon={icons[type] || 'FileEarmark'} className="me-1" /> */}
        {labels[type] || type}
      </span>
    );
  };

  const renderClientInfo = () => {
    // Check if we have client in results
    const client = searchResults?.client;
    const summary = searchResults?.summary;
    if (!client) return null;

    return (
      <div className="bg-light p-4 rounded border mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">


          <div className="d-flex align-items-center">
            <div className="bg-white border rounded p-2 me-3">
              <Icon icon="Person" className="text-primary fs-4" />
            </div>
            <div>
              <h5 className="mb-1">{client.nom}</h5>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{client.type}</span>
                <Badge bg={client.statut === 'client_actif' ? 'success' : 'info'}>
                  {client.statut === 'client_actif' ? 'Client actif' : 'Prospect'}
                </Badge>
              </div>
            </div>
          </div>
          {client?.origin === "odoo" && (
            <div>
              <Button variant="primary" onClick={handleOpenCreationModal}>
                <Icon icon="PersonPlus" className="me-2" />
                Créer le client automatiquement
              </Button>
            </div>
          )}

          {/* Summary badges */}
          {summary && (
            <div className="d-flex gap-2">
              <Badge bg="primary" className="d-flex align-items-center">
                <Icon icon="ListTask" className="me-1" />
                {summary.totalInterventions} intervention(s)
              </Badge>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-md-6">
            <h6 className="text-muted mb-3 d-flex align-items-center">
              <Icon icon="Telephone" className="me-2 text-success" />
              Contacts
            </h6>
            {client.telephones?.map((tel, idx) => (
              <div key={idx} className="d-flex align-items-center mb-2">
                <div className="bg-success bg-opacity-10 rounded-circle p-1 me-2">
                  <Icon icon="Telephone" className="text-success" size="xs" />
                </div>
                <a href={`tel:${tel}`} className="text-decoration-none">
                  {tel}
                </a>
              </div>
            ))}
          </div>
          <div className="col-md-6">
            <h6 className="text-muted mb-3 d-flex align-items-center">
              <Icon icon="Envelope" className="me-2 text-primary" />
              Emails
            </h6>
            {client.emails?.map((email, idx) => (
              <div key={idx} className="d-flex align-items-center mb-2">
                <div className="bg-primary bg-opacity-10 rounded-circle p-1 me-2">
                  <Icon icon="Envelope" className="text-primary" size="xs" />
                </div>
                <a href={`mailto:${email}`} className="text-decoration-none text-truncate">
                  {email}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        {summary && (
          <div className="mt-4 pt-3 border-top">
            <h6 className="text-muted mb-3 d-flex align-items-center">
              <Icon icon="BarChart" className="me-2 text-info" />
              Statistiques
            </h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-2">
                  <p className="text-muted">Par type:</p>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {summary.byType.devis > 0 && (
                      <Badge bg="light" text="dark" className="d-flex align-items-center">
                        <Icon icon="FileEarmarkText" className="me-1" />
                        {summary.byType.devis} devis
                      </Badge>
                    )}
                    {summary.byType.intervention > 0 && (
                      <Badge bg="light" text="dark" className="d-flex align-items-center">
                        <Icon icon="Tools" className="me-1" />
                        {summary.byType.intervention} interventions
                      </Badge>
                    )}
                    {summary.byType.pose > 0 && (
                      <Badge bg="light" text="dark" className="d-flex align-items-center">
                        <Icon icon="Hammer" className="me-1" />
                        {summary.byType.pose} poses
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-2">
                  <p className="text-muted">Par statut:</p>
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {summary.byStatut.planifie > 0 && (
                      <Badge bg="primary" className="d-flex align-items-center">
                        {summary.byStatut.planifie} planifié(s)
                      </Badge>
                    )}
                    {summary.byStatut.en_cours > 0 && (
                      <Badge bg="warning" className="d-flex align-items-center">
                        {summary.byStatut.en_cours} en cours
                      </Badge>
                    )}
                    {summary.byStatut.termine > 0 && (
                      <Badge bg="success" className="d-flex align-items-center">
                        {summary.byStatut.termine} terminé(s)
                      </Badge>
                    )}
                    {summary.byStatut.annule > 0 && (
                      <Badge bg="danger" className="d-flex align-items-center">
                        {summary.byStatut.annule} annulé(s)
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <CreationModal
          show={showClientModal}
          onHide={() => setShowClientModal(false)}
          onClose={() => setShowClientModal(false)}
          title={`Importer ${client?.nom}`}
          fields={clientCreationFields} // Your fields array provided in the prompt
          initialData={{
            nom: client?.nom,
            // Take the first email and phone from Odoo arrays
            emails: client?.emails?.[0] || "",
            telephones: client?.telephones?.[0] || "",
            codeAcces: client?.x_code || "",
            shippingAddresses: client?.shippingAddresses,
            // Pre-fill address fields from the Odoo shipping address mapping
            address: {
              street: client?.shippingAddresses?.[0]?.street || "",
              city: client?.shippingAddresses?.[0]?.city || "",
              zip: client?.shippingAddresses?.[0]?.zip || "",
              country: client?.shippingAddresses?.[0]?.country || "France",
              map_url: client?.shippingAddresses?.[0]?.places || "",
            },
            adresses: searchResults?.client?.shippingAddresses?.[0]?.street || "",
            address_street: searchResults?.client?.shippingAddresses?.[0]?.street || "",
            address_city: searchResults?.client?.shippingAddresses?.[0]?.city || "",
            address_zip: searchResults?.client?.shippingAddresses?.[0]?.zip || "",
            address_country: searchResults?.client?.shippingAddresses?.[0]?.country || "France",
            address_map_url: searchResults?.client?.shippingAddresses?.[0]?.map_url || ""

          }}
          submit={handleCreateClient} // Adjust to your actual API method
          onSuccess={handleClientCreated}
          submitLabel="Enregistrer le client"
        />
      </div>
    );
  };
  const renderInterventions = () => {
    const interventions = searchResults?.interventions || [];

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0 d-flex align-items-center">
            <Icon icon="ListTask" className="me-2 text-primary" />
            Interventions
            <Badge bg="light" text="dark" className="ms-2">
              {interventions.length}
            </Badge>
          </h5>
        </div>

        {interventions.length === 0 ? (
          <div className="text-center py-5 border rounded">
            <Icon icon="Inbox" className="text-muted fs-1 mb-3" />
            <p className="text-muted mb-0">Aucune intervention trouvée pour ce client</p>
          </div>
        ) : (
          <div className="row">
            {interventions.map((intervention) => (
              <div className="col-12" key={intervention._id}>
                <div className="card shadow-sm border-0">
                  <div className="card-body">
                    {/* Header : reference + origin badge + status */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="fw-bold mb-0">{intervention.reference}</h6>
                        {intervention.origin === "odoo" && (
                          <small className="text-danger">Odoo</small>
                        )}
                      </div>
                      {getStatusBadge(intervention.statut)}
                    </div>

                    {/* Type + motif */}
                    <div className="mb-2">
                      {getTypeBadge(intervention.type)}
                      <div className="text-muted small mt-1">{intervention.motif}</div>
                    </div>

                    {/* Dates : full date + time range */}
                    <div className="mb-2 small">
                      <div className="d-flex align-items-center mb-1">
                        <Icon icon="Calendar3" className="me-2 text-success" />
                        {moment(intervention.date_debut).format("DD-MM-YYYY")}
                      </div>
                      <div className="d-flex align-items-center">
                        <Icon icon="CalendarCheck" className="me-2 text-primary" />
                        {moment(intervention.date_debut).format("HH:mm")} -{" "}
                        {moment(intervention.date_fin).format("HH:mm")}
                      </div>
                    </div>

                    {/* Responsable + Techniciens */}
                    <div className="row small mb-2">
                      <div className="col-6">
                        <strong>Créé par:</strong>
                        <br />
                        {intervention.createdBy?.name || "---"}
                      </div>
                      <div className="col-6">
                        <strong>Techniciens:</strong>
                        <br />
                        {intervention?.techniciens?.length > 0 ? (
                          intervention.techniciens.map((tech, idx) => (
                            <div key={tech._id || idx}>{tech.name}</div>
                          ))
                        ) : intervention?.cars?.[0]?.conducteurs ? (
                          intervention.cars[0].conducteurs.map((cond, idx) => (
                            <div key={idx}>{cond.technicien?.name || "Technicien"}</div>
                          ))
                        ) : (
                          "Non assigné"
                        )}
                      </div>
                    </div>

                    {/* Description (notes) */}
                    {/* All notes with authors */}
                    <div className="small text-muted mb-3">
                      {intervention?.notes?.length > 0 ? (
                        intervention.notes.map((noteItem, idx) => (
                          <div key={idx} className="mb-2 pb-2 border-bottom">
                            <div
                              dangerouslySetInnerHTML={{ __html: noteItem.note || "---" }}
                            />
                            {noteItem.createdBy?.name && (
                              <div className="mt-1 text-end">
                                <small className="fst-italic">
                                  — {noteItem.createdBy.name}
                                </small>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        "---"
                      )}
                    </div>

                    {/* Action button */}
                    <div className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewIntervention(intervention)}
                      >
                        <Icon icon="Eye" className="me-1" />
                        {intervention.origin === "odoo" ? "PDF" : "Voir"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleRefreshIntervention = async (interventionId) => {
    try {
      const refreshed = await refreshIntervention(interventionId);
      if (refreshed) {
        setSelectedIntervention(refreshed);
      }
      return refreshed;
    } catch (error) {
      console.error('❌ Erreur rafraîchissement:', error);
      return null;
    }
  };


  const handleUpdateIntervention = async (interventionId, updates) => {
    try {
      const updated = await updateIntervention(interventionId, updates);
      if (updated) {
        // showNotification('Succès', 'Intervention mise à jour!', 'success');
        reloadData();
        return updated;
      }
      throw new Error('Erreur lors de la mise à jour');
    } catch (error) {
      showNotification('Erreur', 'Erreur mise à jour', 'danger');
      throw error;
    }
  };

  const handleDeleteIntervention = async (interventionId) => {
    console.log('deleted');
    try {
      const success = await deleteIntervention(interventionId);
      if (success) {
        showNotification('Succès', 'Intervention supprimée!', 'success');
        handleCloseDetailsModal();
        reloadData();
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      showNotification('Erreur', 'Erreur suppression', 'danger');
    }
  };

  const handleTransferInterventions = async (ids, targetCarId, fromCarId) => {
    try {
      const success = await transferInterventions(ids, targetCarId, fromCarId);
      if (success) {
        showNotification('Succès', 'Transfert réussi!', 'success');
        return true;
      }
      throw new Error('Erreur transfert');
    } catch (error) {
      showNotification('Erreur', 'Erreur transfert', 'danger');
      return false;
    }
  };

  const handleAddChronologieEntry = async (interventionId, entryData) => {
    try {
      const newEntry = await addChronologieEntry(interventionId, entryData);
      if (newEntry) {
        showNotification('Succès', 'Entrée ajoutée!', 'success');
        return newEntry;
      }
      throw new Error('Failed to add entry');
    } catch (error) {
      showNotification('Erreur', 'Erreur ajout entrée', 'danger');
      throw error;
    }
  };

  const handleCreateRapport = async (rapportData) => {
    try {
      const newRapport = await createRapport(rapportData);
      if (newRapport && newRapport._id) {
        showNotification('Succès', 'Rapport créé!', 'success');
        return newRapport;
      }
      throw new Error(newRapport?.message || 'Erreur création rapport');
    } catch (error) {
      showNotification('Erreur', error.message, 'danger');
      throw error;
    }
  };

  const handleUpdateRapport = async (rapportId, updates) => {
    try {
      const updated = await updateRapport(rapportId, updates);
      if (updated) {
        showNotification('Succès', 'Rapport mis à jour!', 'success');
        return updated;
      }
      throw new Error('Erreur mise à jour rapport');
    } catch (error) {
      showNotification('Erreur', 'Erreur mise à jour rapport', 'danger');
    }
  };

  const handleDeleteRapport = async (rapportId) => {
    try {
      const success = await deleteRapport(rapportId);
      if (success) {
        showNotification('Succès', 'Rapport supprimé!', 'success');
      } else {
        throw new Error('Erreur suppression rapport');
      }
    } catch (error) {
      showNotification('Erreur', 'Erreur suppression rapport', 'danger');
    }
  };


  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedIntervention(null);
    setDetailsLoading(false);
  };


  // ============================================================
  // Main render
  // ============================================================
  return (
    <>
      {/* Offcanvas with client info + interventions + optional creation modal */}
      <OffCanvas id="search-results-offcanvas" isOpen={searchModalOpen} setOpen={setSearchModalOpen} placement="end" isBodyScroll isBackdrop={false} >
        <OffCanvasHeader setOpen={setSearchModalOpen}>Détails client - {searchQuery}</OffCanvasHeader>
        <OffCanvasBody>
          {searchLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            <>
              {renderClientInfo()}
              {renderInterventions()}
              {/* Creation modal inside offcanvas for nouveau client */}
              <CreationModal
                show={showCreationModal}
                onHide={() => setShowCreationModal(false)}
                onClose={() => setShowCreationModal(false)}
                title="Créer un nouveau client"
                fields={clientCreationFields}
                initialData={{
                  nom: searchResults?.nom || "",
                  telephones: searchResults?.telephone || "",
                  emails: "",
                }}
                submit={handleCreateClient}
                onSuccess={() => setShowCreationModal(false)}
                submitLabel="Créer le client"
              />
            </>
          )}
        </OffCanvasBody>
      </OffCanvas>

      {selectedIntervention && (
        <InterventionDetailsModal
          show={detailsModalOpen}
          onHide={handleCloseDetailsModal}
          intervention={selectedIntervention}
          cars={cars}
          onUpdateIntervention={handleUpdateIntervention}
          onDeleteIntervention={handleDeleteIntervention}
          CHRONOLOGIE_TYPES={CHRONOLOGIE_TYPES}
          INTERVENTION_TYPES={INTERVENTION_TYPES}
          INTERVENTION_MOTIFS={INTERVENTION_MOTIFS}
          onAddChronologieEntry={handleAddChronologieEntry}
          getChronologieByIntervention={getChronologieByIntervention}
          getRapportByIntervention={getRapportByIntervention}
          getInterventionById={getInterventionById}
          refreshIntervention={handleRefreshIntervention}
          onCreateRapport={handleCreateRapport}
          onUpdateRapport={handleUpdateRapport}
          onDeleteRapport={handleDeleteRapport}
          addPaiement={addPaiement}
          getPaiementsByRapport={getPaiementsByRapport}
          updatePaiement={updatePaiement}
          deletePaiement={deletePaiement}
          setSelectedIntervention={setSelectedIntervention}
          reloadData={reloadData}
        />
      )}

      <Modal show={odooModalOpen} onHide={() => setOdooModalOpen(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            Intervention Odoo {selectedOdooInt?.reference}
            <Badge bg="info" className="ms-2">{selectedOdooInt?.statut}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-4">
            <Col md={6}>
              <h6>📅 Planification</h6>
              <p className="small mb-1"><strong>Début:</strong> {selectedOdooInt?.date_debut}</p>
              <p className="small"><strong>Fin:</strong> {selectedOdooInt?.date_fin}</p>
            </Col>
            <Col md={6}>
              <h6>👥 Techniciens</h6>
              {selectedOdooInt?.techniciens?.map(t => (
                <Badge key={t.id} bg="secondary" className="me-1">{t.name}</Badge>
              ))}
            </Col>
          </Row>

          <hr />

          <h6>📝 Remarques</h6>
          <div className="p-3 bg-light rounded mb-3" style={{ fontSize: '14px' }}>
            <div dangerouslySetInnerHTML={{ __html: selectedOdooInt?.notes?.[0]?.note || '<i>Aucune remarque</i>' }} />
          </div>

          <h6>📝 Rapport technicien</h6>
          <div className="p-3 bg-light rounded mb-3" style={{ fontSize: '14px' }}>
            <div dangerouslySetInnerHTML={{ __html: selectedOdooInt?.rawOdooData?.Rapport || '<i>Aucune rapport</i>' }} />
          </div>
          {/* Images Section */}

          <hr />

          {/* Messages / Discussion Section */}
          <h6>💬 Fil de discussion</h6>
          <div className="message-thread" style={{ maxHeight: '300px', overflowY: 'auto', padding: '10px', backgroundColor: '#f8f9fa' }}>
            {loadingMessages ? (
              <div className="text-center p-3"><Spinner animation="border" size="sm" /></div>
            ) : mailMessages.length > 0 ? (
              mailMessages.map((msg) => (
                <div key={msg.id} className="mb-3 p-2 border-bottom bg-white rounded shadow-sm">
                  {msg.date && (
                    <div className='todo-subtitle mb-3'>
                      {moment(msg.date).fromNow()}
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="fw-bold text-primary" style={{ fontSize: '14px' }}>👤 {msg.author_id?.name}</span>
                    <span className="text-muted" style={{ fontSize: '12px' }}>📅 {msg.date}</span>
                  </div>
                  <div className="" dangerouslySetInnerHTML={{ __html: msg.body }} />
                </div>
              ))
            ) : (
              <p className="text-muted small">Aucun message pour cette intervention.</p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setOdooModalOpen(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}